import { useState, useEffect, useCallback, useRef } from 'react';
import type { ApiKey } from '@yawi/types';
import type { IApiKeyQueryParams, PaginationMeta } from '@yawi/domain';
import { useServices, useToast } from '@/app/providers';

const DEFAULT_LIMIT = 10;

export function useApiKeys(initialParams?: IApiKeyQueryParams) {
  const { apiKeyRepository } = useServices();
  const { showToast } = useToast();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(initialParams?.page ?? 1);
  const [search, setSearch] = useState(initialParams?.search ?? '');
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Solo la última petición escribe el estado (respuestas fuera de orden).
  const requestIdRef = useRef(0);

  const fetchApiKeys = useCallback(async (params?: IApiKeyQueryParams) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
        const response = await apiKeyRepository.findAll(params);
        if (requestId !== requestIdRef.current) return;
        setApiKeys(response.apiKeys || []);
        const totalPages = response.totalPages ?? 0;
        setPagination({
          page: response.page ?? 1,
          limit: response.limit ?? DEFAULT_LIMIT,
          total: response.total ?? 0,
          totalPages,
          hasNextPage: (response.page ?? 1) < totalPages,
          hasPreviousPage: (response.page ?? 1) > 1,
        });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load API keys';
      setError(message);
      showToast('error', message);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [apiKeyRepository, showToast]);

  // La búsqueda viaja al servidor: filtrar `apiKeys` en el componente sólo
  // alcanzaba la página ya cargada mientras el total prometía el conjunto entero.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchApiKeys({
        ...initialParams,
        page,
        limit: DEFAULT_LIMIT,
        search: search.trim() || undefined,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchApiKeys, initialParams, page, search]);

  // El conjunto encoge al escribir: volver a la primera página.
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const goToPage = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const reload = useCallback(
    (params?: IApiKeyQueryParams) =>
      fetchApiKeys({
        ...params,
        page,
        limit: DEFAULT_LIMIT,
        search: search.trim() || undefined,
      }),
    [fetchApiKeys, page, search]
  );

  const revokeApiKey = async (id: string, reason?: string) => {
    try {
      await apiKeyRepository.revoke(id, reason ? { reason } : undefined);
      showToast('success', 'API key revoked successfully');
      await reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke API key';
      showToast('error', message);
    }
  };

  const deleteApiKey = async (id: string) => {
    try {
      await apiKeyRepository.delete(id);
      showToast('success', 'API key deleted successfully');
      await reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete API key';
      showToast('error', message);
    }
  };

  const regenerateApiKey = async (id: string) => {
    try {
      const response = await apiKeyRepository.regenerate(id);
      showToast('success', 'API key regenerated successfully');
      await reload();
      return response.key;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to regenerate API key';
      showToast('error', message);
      return null;
    }
  };

  return {
    apiKeys,
    isLoading,
    error,
    pagination,
    search,
    setSearch: handleSearchChange,
    goToPage,
    refetch: reload,
    revokeApiKey,
    deleteApiKey,
    regenerateApiKey,
  };
}
